import { MapFeatureDetailsOverlaysContainer } from 'features/siteMap/popupMenus/featureDetails/MapFeatureDetailsOverlaysContainer'
import {
  MapInteractionAPIs,
  SiteViewMapProps,
} from 'features/siteMap/SiteViewMap'
import {
  ExtraLabelDisplayFn,
  UnitDisplayPropertiesFn,
} from 'features/siteMapViz'
import React, { FC, useEffect, useRef, useState } from 'react'
import { MapRef } from 'react-map-gl'
import styled from 'styled-components'
import { GeckoGeographicPin, GeckoSite, GeckoUnit } from 'types'

import { useHandleCameraFlight } from './camera/useHandleCameraFlight'
import { MapboxModebar } from './mapActions/modebar/MapboxModebar'
import { SiteBoundsEditor } from './mapActions/siteBoundsEditor/SiteBoundsEditor'
import { MapboxFeatureDispatcher } from './mapFeatures/MapboxFeatureDispatcher'
import { SiteFocusedMap } from './SiteFocusedMap'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

interface MapBoxMapProps {
  site: GeckoSite
  allUnitsAtSite: ReadonlyArray<GeckoUnit>
  getUnitDisplayProperties: UnitDisplayPropertiesFn
  shouldDisplayTaggedUnit: NonNullable<
    MapInteractionAPIs['shouldDisplayTaggedUnit']
  >
  unitIDFlyingTo: string | undefined
  setUnitIDFlyingTo: (unitID: string | undefined) => void

  /**
   * Function that will return an extra react-node to be displayed in the label
   * for an asset
   *
   * Note: This function needs to be appropriately memoized
   * Note: The React-Node returned cannot use contexts -- React-Three-Fiber
   *        appears to be rendering this outside of our react-tree so it will
   *        fail if you do
   *        TODO [ARCH-330]: Make labels work with contexts
   */
  getExtraLabelDisplay: ExtraLabelDisplayFn
  componentToDisplayOnFeatureClick: SiteViewMapProps['componentToDisplayOnFeatureClick']
  pins: readonly GeckoGeographicPin[]
}
export const MapBoxMapViz: FC<MapBoxMapProps> = (props) => {
  const [site, setSite] = useState<GeckoSite>(props.site)
  useEffect(() => {
    setSite(props.site)
  }, [props.site])

  const mapRef = useRef<MapRef>(null)
  const [isEditSiteBoundsBarOpen, setIsEditSiteBoundsBarOpen] = useState(false)

  useHandleCameraFlight(
    props.unitIDFlyingTo,
    props.setUnitIDFlyingTo,
    props.allUnitsAtSite,
    mapRef
  )

  return (
    <Container>
      <SiteBoundsEditor
        site={site}
        isUiVisible={isEditSiteBoundsBarOpen}
        mapRef={mapRef}
        setSite={setSite}
      />
      <SiteFocusedMap ref={mapRef} site={site}>
        <MapboxModebar
          isInEditSiteBoundsMode={isEditSiteBoundsBarOpen}
          setIsInEditSiteBoundsMode={setIsEditSiteBoundsBarOpen}
        />
        <MapFeatureDetailsOverlaysContainer
          componentToDisplayOnFeatureClick={
            props.componentToDisplayOnFeatureClick
          }
        />
        <MapboxFeatureDispatcher
          units={props.allUnitsAtSite}
          pins={props.pins}
          getUnitDisplayProperties={props.getUnitDisplayProperties}
          shouldDisplayTaggedUnit={props.shouldDisplayTaggedUnit}
          getExtraLabelDisplay={props.getExtraLabelDisplay}
        />
      </SiteFocusedMap>
    </Container>
  )
}
